import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { Dictionary } from '@ngrx/entity';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import {
    type NormalizedColumnEntity,
    type NormalizedSectionEntity,
    type WebLegacyWidgetEntity,
    type WebWidgetEntity,
} from '@web/types';

import { Widgets } from '@common/enums';
import { isLegacyWidget, widgetNameOf, widgetParentIdOf } from '@common/helpers/widget-envelope';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';

import { SettingsBreadcrumbsComponent } from '@web/shared/components/web-settings/settings-breadcrumbs/settings-breadcrumbs.component';
import { AbstractBaseSettingsComponent } from '../../abstract-base-settings/abstract-base-settings.component';

/** Knowledge-base article per widget; widgets missing here show no link. */
const KNOWLEDGE_BASE_LINKS: Partial<Record<Widgets, string>> = {
    [Widgets.BUTTON]: 'knowledge_base_link_button',
    [Widgets.IMAGE]: 'knowledge_base_link_image',
    [Widgets.LOGO]: 'knowledge_base_link_logo',
    [Widgets.GALLERY]: 'knowledge_base_link_gallery',
    [Widgets.LINE]: 'knowledge_base_link_line',
    [Widgets.INDENT]: 'knowledge_base_link_indent',
    [Widgets.TEXT]: 'knowledge_base_link_text',
    [Widgets.VIDEO]: 'knowledge_base_link_video',
    [Widgets.FORM]: 'knowledge_base_link_form',
    [Widgets.PAYMENT_FORM]: 'knowledge_base_link_payment',
    [Widgets.CHAT_BOTS]: 'knowledge_base_link_chatbots',
    [Widgets.SOCIAL_NETWORKS]: 'knowledge_base_link_socials',
    [Widgets.MENU]: 'knowledge_base_link_menu',
    [Widgets.NAVIGATION]: 'knowledge_base_link_navigation',
    [Widgets.MAP]: 'knowledge_base_link_map',
    [Widgets.ACCORDION]: 'knowledge_base_link_faq',
    [Widgets.TIMER]: 'knowledge_base_link_timer',
    [Widgets.LOGIN_EDU]: 'knowledge_base_link_login_edu',
    [Widgets.CODE]: 'knowledge_base_link_code',
    [Widgets.TESTIMONIAL]: 'knowledge_base_link_testimonial',
    [Widgets.PRODUCT_CARD]: 'knowledge_base_link_product_card',
    [Widgets.SHOP_CART]: 'knowledge_base_link_shop_cart',
    [Widgets.BEFORE_AFTER]: 'knowledge_base_link_before_after',
    [Widgets.CATALOG_PRODUCTS]: 'knowledge_base_link_catalog_products',
    [Widgets.FIGURE]: 'knowledge_base_link_figure',
};

@Component({
    selector: 'web-widget-breadcrumbs',
    templateUrl: './widget-breadcrumbs.component.html',
    styleUrls: ['./widget-breadcrumbs.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SettingsBreadcrumbsComponent, TooltipModule, TranslateModule],
})
export class WidgetBreadcrumbsComponent extends AbstractBaseSettingsComponent {
    public readonly widget = input.required<WebLegacyWidgetEntity | WebWidgetEntity>();

    /**
     * The widget sits in a `specific` section — the header. Its trail walks
     * header → row instead of the regular block → column → structure.
     */
    public readonly isInSpecificSection = input(false, { transform: booleanAttribute });
    public readonly isEduSite = input(false, { transform: booleanAttribute });
    public readonly hideBreadcrumbs = input(false, { transform: booleanAttribute });

    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly gridsFacade: GridsFacade = inject(GridsFacade);
    private readonly tabsFacade: TabsFacade = inject(TabsFacade);

    private readonly sections = toSignal(this.sectionsFacade.sectionsEntities$, {
        initialValue: {} as Dictionary<NormalizedSectionEntity>,
    });
    private readonly columns = toSignal(this.columnsFacade.columnsEntities$, {
        initialValue: {} as Dictionary<NormalizedColumnEntity>,
    });

    private readonly grids = toSignal(this.gridsFacade.gridsEntities$, { initialValue: {} });
    private readonly tabs = toSignal(this.tabsFacade.tabsEntities$, { initialValue: {} });

    /**
     * A legacy widget names its column directly. Otherwise the widget only knows its direct
     * parent, which for a grid cell is the grid — so walk grid → (tab →) column.
     */
    public readonly column = computed<NormalizedColumnEntity | undefined>(() => {
        const widget = this.widget();
        let parentId = widgetParentIdOf(widget);

        if (isLegacyWidget(widget)) {
            return this.columns()[parentId];
        }

        for (let depth = 0; parentId && depth < 3; depth++) {
            const column = this.columns()[parentId];
            if (column) {
                return column;
            }
            parentId = (this.grids()[parentId] ?? this.tabs()[parentId])?.parentId;
        }

        return undefined;
    });

    /** The grid the widget sits in, if any: legacy `structureId`, or a `parentId` that names a grid. */
    public readonly gridId = computed<string | undefined>(() => {
        const widget = this.widget();

        if (isLegacyWidget(widget)) {
            return (widget as WebLegacyWidgetEntity).structureId;
        }

        const parentId = widgetParentIdOf(widget);
        return this.grids()[parentId] ? parentId : undefined;
    });

    public readonly section = computed<NormalizedSectionEntity | undefined>(() => {
        const column = this.column();
        return column ? this.sections()[column.parentId] : undefined;
    });

    /** The widget kind, from either envelope (legacy `name`, versioned `widget:<kind>`). */
    public readonly widgetName = computed<Widgets>(() => widgetNameOf(this.widget()));

    public readonly widgetLabel = computed<string>(() => {
        const widgetName = this.widgetName();

        if (widgetName === Widgets.PAYMENT_FORM && this.isEduSite()) {
            return 'tariffs_widget_label';
        }

        return this.widgetsService.getWidgetModel(widgetName)?.label;
    });

    public readonly knowledgeBaseLink = computed<string | undefined>(() => KNOWLEDGE_BASE_LINKS[this.widgetName()]);

    // The base class builds a settings FormGroup for its other subclasses; this
    // one is display-only and has no form of its own.
    protected initForm(): void {}
}
